import { Api } from '@/Api';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';

export interface TemplateFormData {
  templateSlot?: string;
  templateName: string;
  templateDescription?: string;
  defaultTitle: string;
  defaultDescription?: string;
  commentEnabled: boolean;
  reactionEnabled: boolean;
  buttonEnabled: boolean;
  buttonText?: string;
  buttonLink?: string;
  belongsToOrg: boolean;
  // Security settings
  securitySetting?: string;
  expireDaysEnabled?: boolean;
  expireDays?: number;
  // Collections
  collections?: string[];
}

export interface Template {
  id: string;
  slot: string;
  name: string;
  description?: string;
  itemTitle: string;
  itemDescription?: string;
  reactionEnabled: boolean;
  commentEnabled: boolean;
  buttonEnabled: boolean;
  buttonText?: string;
  buttonLink?: string;
  belongsToOrg: boolean;
  isDefault: boolean;
  userId: string;
  collections?: string[];
  // Expires
  expireDaysEnabled?: boolean;
  expireDays?: number;
  // security_setting
  securitySetting?: string;
  // CTA
  callToActionId?: string;
}

export interface TemplateState {
  templates: Template[];
  loading: boolean;
}

// Default template configurations (static data)
const DEFAULT_TEMPLATE_CONFIGS = {
  sales: {
    slot: 'sales',
    name: 'Sales Intro',
    itemTitle: 'Intro from [Your Company Name Here]',
    reactionEnabled: true,
    commentEnabled: true,
    buttonEnabled: true,
    buttonText: 'Book a Meeting',
    buttonLink: 'https://calendly.com/URL_HERE',
  },
  bug: {
    slot: 'bug',
    name: 'Bug Report',
    itemTitle: 'Bug Report - [Issue Name/Detail]',
    reactionEnabled: true,
    commentEnabled: true,
    buttonEnabled: true,
    buttonText: 'View Issue',
    buttonLink: 'https://YOUR_COMPANY.atlassian.net/browse/LINKS_HERE',
  },
  support: {
    slot: 'support',
    name: 'Support Response',
    itemTitle: 'Response - Ticket [Number Here]',
    reactionEnabled: true,
    commentEnabled: true,
    buttonEnabled: true,
    buttonText: 'Visit Help Center',
    buttonLink: 'https://support.zight.com/',
  },
  announcement: {
    slot: 'announcement',
    name: 'Announcement',
    itemTitle: 'Announcing - [Project/Product Name Here]',
    reactionEnabled: true,
    commentEnabled: true,
    buttonEnabled: true,
    buttonText: 'Learn More',
    buttonLink: 'https://www.zight.com/blog/new-features-collaboration',
  },
} as const;

const DEFAULT_SLOTS = Object.keys(DEFAULT_TEMPLATE_CONFIGS);

const { debug } = useDebug(DEBUG_NAMESPACE.VIEWER_TEMPLATE);

export const useViewerTemplateStore = defineStore('viewerTemplateStore', {
  state: (): TemplateState => ({
    templates: [],
    loading: false,
  }),

  actions: {
    async fetchTemplates() {
      this.loading = true;

      try {
        const response = await Api.template.index();
        const rawTemplates = response.data?.templates || [];
        const includedCtas = response.included || [];

        // Create a map of CTA data by ID for quick lookup
        const ctaMap = new Map();
        includedCtas.forEach((cta: any) => {
          if (cta.type === 'call_to_action') {
            ctaMap.set(cta.id, cta.attributes);
          }
        });

        // Transform API response to flattened structure
        this.templates = rawTemplates.map((template: any) => {
          const ctaData = ctaMap.get(template.attributes.callToActionId);

          return {
            id: template.id,
            ...template.attributes,
            buttonText: ctaData?.text || '',
            buttonLink: ctaData?.link || '',
          };
        });

        debug('[fetchTemplates] Templates fetched:', this.templates);
        return this.templates;
      } catch (error) {
        debug('[fetchTemplates] Error fetching templates:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async applyTemplate(templateId: string, itemId: string) {
      try {
        const response = await Api.template.apply(templateId, itemId);

        debug('[applyTemplate] Template applied:', { templateId, itemId });

        // Reload item data to get updated CTA
        const { useViewerItemStore } = await import('./useViewerItemStore');
        const itemStore = useViewerItemStore();
        const itemResponse = await Api.item.get(itemId);
        itemStore.initialize(itemResponse.data);

        // Reload CTA if present
        const { ctaId } = itemStore;
        if (ctaId) {
          const { useCallToActionStore } = await import('./useCallToActionStore');
          const ctaStore = useCallToActionStore();

          // Force reload by resetting isLoaded flag
          ctaStore.$patch({ isLoaded: false });
          await ctaStore.getCallToAction(ctaId);
        }
        return response.data;
      } catch (error) {
        debug('[applyTemplate] Error applying template:', error);
        throw error;
      }
    },

    async saveTemplate(templateData: TemplateFormData) {
      try {
        // Prepare API payload - ApiHelpers will convert to snake_case
        const payload = {
          template: {
            slot: templateData.templateSlot || this.generateUniqueSlot(),
            name: templateData.templateName,
            description: templateData.templateDescription,
            itemTitle: templateData.defaultTitle,
            itemDescription: templateData.defaultDescription,
            commentEnabled: templateData.commentEnabled,
            reactionEnabled: templateData.reactionEnabled,
            buttonEnabled: templateData.buttonEnabled,
            expireDaysEnabled: templateData.expireDaysEnabled,
            expireDays: templateData.expireDays,
            securitySetting: templateData.securitySetting,
            collections: templateData.collections,
            belongsToOrg: templateData.belongsToOrg,
          },
          cta: templateData.buttonEnabled
            ? {
                text: templateData.buttonText || '',
                link: templateData.buttonLink || '',
              }
            : undefined,
        };

        const response = await Api.template.save(payload);
        const savedTemplate = response.data?.template;
        const includedCtas = response.included || [];

        if (savedTemplate) {
          // Find CTA data from included
          const ctaData = includedCtas.find((item: any) => item.type === 'callToAction' && item.id === savedTemplate.attributes?.callToActionId);

          // Transform to Template format
          const transformedTemplate: Template = {
            id: savedTemplate.id,
            ...savedTemplate.attributes,
            buttonText: ctaData?.attributes?.text || '',
            buttonLink: ctaData?.attributes?.link || '',
          };

          // Update local state
          const existingIndex = this.templates.findIndex(t => t.id === transformedTemplate.id);
          if (existingIndex >= 0) {
            this.templates[existingIndex] = transformedTemplate;
          } else {
            this.templates.push(transformedTemplate);
          }

          debug('[saveTemplate] Template saved:', transformedTemplate);
          return transformedTemplate;
        }

        throw new Error('No template data in response');
      } catch (error) {
        debug('[saveTemplate] Error saving template:', error);
        throw error;
      }
    },

    async updateDefaultTemplate(template: Template) {
      try {
        let response;
        if (template.isDefault) {
          // Remove as default - use templateId for API
          response = await Api.template.removeDefault(template.id);
        } else {
          // Set as default - use templateId for API
          response = await Api.template.setDefault(template.id);
        }

        // Update templates using response data with immutable updates
        if (response.data?.template) {
          const { template: updatedTemplate } = response.data;

          this.templates = this.templates.map(existingTemplate => {
            if (existingTemplate.id === updatedTemplate.id) {
              return {
                ...existingTemplate,
                isDefault: updatedTemplate.attributes?.isDefault || false,
              };
            } else {
              return {
                ...existingTemplate,
                isDefault: false,
              };
            }
          });
        }

        debug('[updateDefaultTemplate] Updated default template for slot:', template.slot);
        return response.data?.template || template;
      } catch (error) {
        debug('[updateDefaultTemplate] Error updating default template:', error);
        throw error;
      }
    },
  },

  getters: {
    isLoading: state => state.loading,

    getDefaultTemplateConfig: () => (slot: string) => {
      return DEFAULT_TEMPLATE_CONFIGS[slot as keyof typeof DEFAULT_TEMPLATE_CONFIGS] || null;
    },

    getTemplateBySlot:
      state =>
      (slot: string): Template | null => {
        return state.templates.find(t => t.slot === slot) || null;
      },

    getTemplateOrDefaults() {
      return (slot: string) => {
        return this.getTemplateBySlot(slot) || this.getDefaultTemplateConfig(slot);
      };
    },

    isTemplateConfigured:
      state =>
      (slot: string): boolean => {
        return !!state.templates.find(t => t.slot === slot);
      },

    isTemplateOwner:
      () =>
      (template: Template, userId: string): boolean => {
        return template.userId === userId;
      },

    customTemplates: state => {
      return state.templates.filter(template => !DEFAULT_SLOTS.includes(template.slot));
    },

    isCustomTemplate:
      () =>
      (slot: string): boolean => {
        return !DEFAULT_SLOTS.includes(slot);
      },

    // Utility functions
    generateUniqueSlot: () => (): string => {
      return uuidv4();
    },
  },
});
